import React from "react";

const PrivacyPolicy360Vitals = () => {
  const sections = [
    {
      number: "1",
      title: "Introduction",
      content: (
        <p className="text-gray-600 leading-relaxed">
          Welcome to <strong>360Vitals</strong>. Your privacy is important to us. This Privacy
          Policy explains how we collect, use, and protect your information.
        </p>
      ),
    },
    {
      number: "2",
      title: "Information We Collect",
      content: (
        <>
          <p className="text-gray-600 leading-relaxed mb-2 font-medium">a. Personal Information</p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2 mb-4">
            <li>Name (if provided)</li>
            <li>Email address (for login/signup)</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mb-2 font-medium">b. Health &amp; Usage Data</p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
            <li>Sleep duration and patterns</li>
            <li>Water intake</li>
            <li>Food and calorie logs</li>
            <li>App usage interactions</li>
          </ul>
        </>
      ),
    },
    {
      number: "3",
      title: "How We Use Your Data",
      content: (
        <>
          <p className="text-gray-600 leading-relaxed mb-3">We use your data to:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
            <li>Provide personalized health insights</li>
            <li>Improve AI Coach recommendations</li>
            <li>Enhance app performance and features</li>
            <li>Send reminders and notifications</li>
          </ul>
        </>
      ),
    },
    {
      number: "4",
      title: "AI Coach Usage",
      content: (
        <p className="text-gray-600 leading-relaxed">
          Your data is analyzed by our AI systems to generate suggestions. These are based on
          patterns and are <strong>not medical advice</strong>.
        </p>
      ),
    },
    {
      number: "5",
      title: "Data Sharing",
      content: (
        <>
          <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
            <li>We do <strong>not</strong> sell your personal data.</li>
            <li>
              We may share data with trusted service providers (for app functionality) and legal
              authorities if required by law.
            </li>
          </ul>
        </>
      ),
    },
    {
      number: "6",
      title: "Data Security",
      content: (
        <p className="text-gray-600 leading-relaxed">
          We use industry-standard security practices to protect your data from unauthorized
          access, loss, or misuse.
        </p>
      ),
    },
    {
      number: "7",
      title: "Data Retention",
      content: (
        <p className="text-gray-600 leading-relaxed">
          Your data is stored only as long as necessary to provide services or comply with
          legal obligations.
        </p>
      ),
    },
    {
      number: "8",
      title: "Your Rights",
      content: (
        <>
          <p className="text-gray-600 leading-relaxed mb-3">You have the right to:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
            <li>Access your data</li>
            <li>Update or correct your data</li>
            <li>Delete your account and associated data</li>
          </ul>
        </>
      ),
    },
    {
      number: "9",
      title: "Third-Party Services",
      content: (
        <p className="text-gray-600 leading-relaxed">
          360Vitals may integrate with third-party services (e.g., authentication providers).
          Their own privacy policies may apply to data they process.
        </p>
      ),
    },
    {
      number: "10",
      title: "Children's Privacy",
      content: (
        <p className="text-gray-600 leading-relaxed">
          360Vitals is <strong>not</strong> intended for users under{" "}
          <strong>13 years of age</strong>. We do not knowingly collect data from children.
        </p>
      ),
    },
    {
      number: "11",
      title: "Changes to This Policy",
      content: (
        <p className="text-gray-600 leading-relaxed">
          We may update this Privacy Policy from time to time. Any updates will be reflected
          within the app. We encourage you to review this policy periodically.
        </p>
      ),
    },
    {
      number: "12",
      title: "Contact Us",
      content: (
        <p className="text-gray-600 leading-relaxed">
          If you have any questions about this Privacy Policy, please contact us at:{" "}
          <a
            href="mailto:projects@datadna.in"
            className="text-green-600 hover:text-green-800 underline font-medium break-all"
          >
            projects@datadna.in
          </a>
        </p>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-green-700 to-green-500 py-14 px-4 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
          Privacy Policy
        </h1>
        <p className="text-green-100 text-base sm:text-lg font-medium">360Vitals App</p>
        <p className="text-green-200 text-sm mt-2">Last Updated: March 2026</p>
      </div>

      {/* Intro Card */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8 border-l-4 border-green-500">
          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            This Privacy Policy explains how <strong>360Vitals</strong> collects, uses, and
            protects user information. We are committed to safeguarding your privacy and
            ensuring transparency in how we handle your data.
          </p>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        {sections.map((section) => (
          <div
            key={section.number}
            className="p-6 sm:p-8 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-start gap-4">
              {/* Number Badge */}
              <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                {section.number}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                  {section.title}
                </h2>
                {section.content}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer Note */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-green-50 p-6 text-center border">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} DATADNA · 360Vitals App · All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy360Vitals;